import React, { useState, useEffect, useMemo, Fragment } from "react";
import { useApp } from "../context/AppContext";

const STATUS_META = {
  done:    { label: "Готово",     bg: "rgba(16,185,129,0.15)",  color: "#10b981" },
  running: { label: "Виконується", bg: "rgba(99,102,241,0.15)",  color: "#6366f1" },
  pending: { label: "Очікує",     bg: "rgba(245,158,11,0.15)",  color: "#f59e0b" },
  error:   { label: "Помилка",    bg: "rgba(239,68,68,0.15)",   color: "#ef4444" },
};

function scoreColor(s) {
  if (s == null) return "#9ca3af";
  if (s >= 90) return "#10b981";
  if (s >= 70) return "#a855f7";
  if (s >= 50) return "#f59e0b";
  return "#ef4444";
}

function relativeTime(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return `${Math.floor(diff)} с тому`;
  if (diff < 3600) return `${Math.floor(diff / 60)} хв тому`;
  if (diff < 86400) return `${Math.floor(diff / 3600)} год тому`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)} дн тому`;
  return d.toLocaleDateString("uk-UA");
}

function countryFlag(code) {
  if (!code || code.length !== 2) return "";
  const base = 0x1F1E6;
  const c = code.toUpperCase();
  return String.fromCodePoint(base + (c.charCodeAt(0) - 65)) + String.fromCodePoint(base + (c.charCodeAt(1) - 65));
}
function deviceIcon(type) {
  if (type === "mobile") return "📱";
  if (type === "tablet") return "📲";
  return "💻";
}

export default function Spalah360LeadsView() {
  const { apiFetch, flash, dark, t, card, inp, btnP, btnG, btnSm } = useApp();

  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [copied, setCopied] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [tab, setTab] = useState("seo"); // "seo" | "ppc"

  const load = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (search) qs.set("search", search);
      if (statusFilter) qs.set("status", statusFilter);
      const base = tab === "ppc" ? "/api/spalah360-admin/ppc" : "/api/spalah360-admin";
      const [r1, r2] = await Promise.all([
        apiFetch(`${base}?${qs.toString()}`),
        apiFetch(`${base}/stats`),
      ]);
      const d1 = await r1.json();
      const d2 = await r2.json();
      if (r1.ok && Array.isArray(d1)) setLeads(d1);
      if (r2.ok) setStats(d2);
    } catch (e) {
      flash("Помилка завантаження");
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Debounced search
  useEffect(() => {
    const tm = setTimeout(load, 300);
    return () => clearTimeout(tm);
    // eslint-disable-next-line
  }, [search, statusFilter, tab]);

  const copyToClipboard = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1200);
    } catch {}
  };

  const exportCsv = () => {
    const header = [
      "created_at", "domain", "phone", "email", "status", "score_overall",
      "ip_address", "cf_country", "cf_city", "cf_region",
      "browser", "browser_version", "os", "os_version", "device_type", "device_model",
      "accept_language", "referrer",
      "utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content",
      "user_agent", "token",
    ];
    const rows = leads.map(l => header.map(h => {
      const v = l[h] == null ? "" : String(l[h]);
      return /[",\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
    }).join(","));
    const csv = [header.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `spalah360-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const filtered = useMemo(() => leads, [leads]);

  const cellTd = { padding: "10px 12px", fontSize: 13, color: t.text, borderBottom: `1px solid ${t.border}`, verticalAlign: "middle" };
  const cellTh = { padding: "10px 12px", fontSize: 11, fontWeight: 600, color: t.textMuted, textTransform: "uppercase", letterSpacing: 0.5, textAlign: "left", borderBottom: `1px solid ${t.border}`, background: dark ? "rgba(255,255,255,0.02)" : "rgba(0,0,0,0.02)" };

  return (
    <div style={{ padding: 24, maxWidth: 1300, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: t.text, letterSpacing: "-0.02em" }}>
            🎯 Spalah — Ліди
          </h1>
          <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4 }}>
            {tab === "ppc" ? "Кожен розрахунок на spalah.app/ppc" : "Кожен запуск публічного аудиту на spalah.app/360"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={load} style={{ ...btnG, height: 34 }}>↻ Оновити</button>
          <button onClick={exportCsv} style={{ ...btnP, height: 34 }} disabled={!leads.length}>↓ CSV</button>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        {[["seo", "🔍 SEO /360"], ["ppc", "⚡ PPC /ppc"]].map(([k, label]) => (
          <button key={k} onClick={() => { if (tab !== k) { setTab(k); setStats(null); setLeads([]); setExpanded({}); } }}
            style={{
              padding: "8px 18px", borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: "pointer", fontFamily: "inherit",
              border: `1px solid ${tab === k ? "#a855f7" : t.border}`,
              background: tab === k ? "linear-gradient(135deg,#a855f7,#ec4899)" : "transparent",
              color: tab === k ? "#fff" : t.textMuted,
            }}>{label}</button>
        ))}
      </div>

      {/* Stats row */}
      {stats && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 18 }}>
          <StatCard label="Всього" value={stats.total} color="#a855f7" t={t} />
          <StatCard label="За 24 год" value={stats.last_24h} color="#6366f1" t={t} />
          <StatCard label="За тиждень" value={stats.last_7d} color="#3b82f6" t={t} />
          <StatCard label="За місяць" value={stats.last_30d} color="#10b981" t={t} />
          <StatCard label={tab === "ppc" ? "Унік. телефонів" : "Унік. emails"} value={tab === "ppc" ? stats.unique_phones : stats.unique_emails} color="#ec4899" t={t} />
          <StatCard label="Унік. доменів" value={stats.unique_domains} color="#f59e0b" t={t} />
        </div>
      )}

      {/* Filters */}
      <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap", alignItems: "center" }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder={tab === "ppc" ? "🔍 Пошук за доменом чи телефоном…" : "🔍 Пошук за доменом, email чи телефоном…"}
          style={{ ...inp, flex: 1, minWidth: 240, height: 34 }}
        />
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ ...inp, width: 160, height: 34 }}>
          <option value="">Усі статуси</option>
          <option value="done">Готово</option>
          <option value="running">Виконується</option>
          <option value="pending">Очікує</option>
          <option value="error">Помилка</option>
        </select>
        <span style={{ fontSize: 12, color: t.textMuted, fontFamily: "monospace" }}>
          {loading ? "..." : `${filtered.length} зап.`}
        </span>
      </div>

      {/* Table */}
      <div style={{ ...card, padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr>
                <th style={cellTh}>Час</th>
                <th style={cellTh}>Домен</th>
                <th style={cellTh}>Телефон</th>
                <th style={cellTh}>Email</th>
                <th style={cellTh}>Статус</th>
                <th style={cellTh}>Бал</th>
                <th style={cellTh}>Гео</th>
                <th style={cellTh}>Девайс</th>
                <th style={cellTh}>UTM</th>
                <th style={cellTh}>Дії</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(lead => {
                const sm = STATUS_META[lead.status] || STATUS_META.pending;
                const cleanDom = (lead.domain || "").replace(/^https?:\/\//, "").replace(/\/$/, "");
                return (
                  <Fragment key={lead.token}>
                  <tr>
                    <td style={cellTd}>
                      <div style={{ fontSize: 13, color: t.text }}>{relativeTime(lead.created_at)}</div>
                      <div style={{ fontSize: 11, color: t.textMuted, fontFamily: "monospace", marginTop: 2 }}>
                        {lead.created_at ? new Date(lead.created_at).toLocaleString("uk-UA") : ""}
                      </div>
                    </td>
                    <td style={cellTd}>
                      <a
                        href={lead.domain}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ color: t.text, textDecoration: "none", fontWeight: 600, fontSize: 13 }}
                      >{cleanDom}</a>
                    </td>
                    <td style={cellTd}>
                      {lead.phone ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <a href={`tel:${lead.phone}`} style={{ color: "#10b981", textDecoration: "none", fontSize: 13, fontWeight: 600, fontFamily: "monospace" }}>
                            {lead.phone}
                          </a>
                          <button
                            onClick={() => copyToClipboard(lead.phone, `phone-${lead.token}`)}
                            title="Копіювати телефон"
                            style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 11, padding: 2 }}
                          >{copied === `phone-${lead.token}` ? "✓" : "📋"}</button>
                        </span>
                      ) : <span style={{ color: t.textMuted, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={cellTd}>
                      {lead.email ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                          <a href={`mailto:${lead.email}`} style={{ color: "#6366f1", textDecoration: "none", fontSize: 13 }}>
                            {lead.email}
                          </a>
                          <button
                            onClick={() => copyToClipboard(lead.email, `email-${lead.token}`)}
                            title="Копіювати email"
                            style={{ background: "none", border: "none", cursor: "pointer", color: t.textMuted, fontSize: 11, padding: 2 }}
                          >{copied === `email-${lead.token}` ? "✓" : "📋"}</button>
                        </span>
                      ) : <span style={{ color: t.textMuted, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={cellTd}>
                      <span style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        padding: "3px 8px", borderRadius: 999,
                        background: sm.bg, color: sm.color,
                        fontSize: 11, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5,
                      }}>{sm.label}</span>
                      {lead.error && (
                        <div title={lead.error} style={{ fontSize: 11, color: "#ef4444", marginTop: 4, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lead.error}
                        </div>
                      )}
                      {lead.status === "running" && lead.progress_message && (
                        <div style={{ fontSize: 11, color: t.textMuted, marginTop: 4, maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {lead.progress_message}
                        </div>
                      )}
                    </td>
                    <td style={cellTd}>
                      {lead.score_overall != null ? (
                        <span style={{ fontWeight: 700, fontSize: 16, color: scoreColor(lead.score_overall), fontFamily: "monospace" }}>
                          {lead.score_overall}
                        </span>
                      ) : <span style={{ color: t.textMuted, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={cellTd}>
                      {lead.cf_country ? (
                        <div>
                          <div style={{ fontSize: 14, color: t.text }}>
                            {countryFlag(lead.cf_country)} {lead.cf_country}
                          </div>
                          {(lead.cf_city || lead.cf_region) && (
                            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                              {[lead.cf_city, lead.cf_region].filter(Boolean).join(", ")}
                            </div>
                          )}
                          <div style={{ fontSize: 10, color: t.textMuted, fontFamily: "monospace", marginTop: 2 }}>{lead.ip_address || "—"}</div>
                        </div>
                      ) : (
                        <span style={{ fontSize: 11, color: t.textMuted, fontFamily: "monospace" }}>{lead.ip_address || "—"}</span>
                      )}
                    </td>
                    <td style={cellTd}>
                      {lead.browser || lead.os ? (
                        <div>
                          <div style={{ fontSize: 13, color: t.text, display: "flex", alignItems: "center", gap: 5 }}>
                            <span>{deviceIcon(lead.device_type)}</span>
                            <span>{lead.browser}{lead.browser_version ? ` ${lead.browser_version.split(".")[0]}` : ""}</span>
                          </div>
                          <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2 }}>
                            {lead.os}{lead.os_version ? ` ${lead.os_version}` : ""}
                            {lead.device_model ? ` · ${lead.device_model}` : ""}
                          </div>
                        </div>
                      ) : <span style={{ color: t.textMuted, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={cellTd}>
                      {(lead.utm_source || lead.utm_campaign) ? (
                        <div>
                          {lead.utm_source && (
                            <div style={{ fontSize: 12, color: "#a855f7", fontWeight: 600 }}>{lead.utm_source}</div>
                          )}
                          {lead.utm_campaign && (
                            <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={lead.utm_campaign}>{lead.utm_campaign}</div>
                          )}
                          {lead.utm_medium && (
                            <div style={{ fontSize: 10, color: t.textMuted, marginTop: 1 }}>{lead.utm_medium}</div>
                          )}
                        </div>
                      ) : lead.referrer ? (
                        <div style={{ fontSize: 11, color: t.textMuted, maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={lead.referrer}>
                          ↳ {(() => { try { return new URL(lead.referrer).hostname; } catch { return lead.referrer; } })()}
                        </div>
                      ) : <span style={{ color: t.textMuted, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={cellTd}>
                      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                        <a
                          href={`/${tab === "ppc" ? "ppc" : "360"}/r/${lead.token}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ ...btnSm, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 4 }}
                        >Звіт →</a>
                        <button
                          onClick={() => setExpanded(p => ({ ...p, [lead.token]: !p[lead.token] }))}
                          title="Деталі"
                          style={{ ...btnSm, background: "transparent", border: `1px solid ${t.border}`, color: t.textMuted, cursor: "pointer", padding: "3px 8px", fontSize: 11 }}
                        >{expanded[lead.token] ? "▴" : "▾"}</button>
                      </div>
                    </td>
                  </tr>
                  {expanded[lead.token] && (
                    <tr>
                      <td colSpan={10} style={{ ...cellTd, padding: "14px 18px", background: dark ? "rgba(168,85,247,0.04)" : "rgba(168,85,247,0.03)" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 14, fontSize: 12 }}>
                          <DetailItem label="User-Agent" value={lead.user_agent} t={t} mono />
                          <DetailItem label="Referrer" value={lead.referrer || "(direct)"} t={t} mono />
                          <DetailItem label="Accept-Language" value={lead.accept_language} t={t} mono />
                          <DetailItem label="UTM term" value={lead.utm_term} t={t} />
                          <DetailItem label="UTM content" value={lead.utm_content} t={t} />
                          <DetailItem label="Token" value={lead.token} t={t} mono />
                        </div>
                      </td>
                    </tr>
                  )}
                  </Fragment>
                );
              })}
              {!loading && !filtered.length && (
                <tr>
                  <td colSpan={10} style={{ ...cellTd, textAlign: "center", padding: 40, color: t.textMuted }}>
                    {search || statusFilter ? "Нічого не знайдено за фільтрами" : "Ще немає лідів"}
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={10} style={{ ...cellTd, textAlign: "center", padding: 40, color: t.textMuted }}>
                    Завантаження…
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function StatCard({ label, value, color, t }) {
  return (
    <div style={{
      padding: "12px 14px",
      background: `${color}12`,
      border: `1px solid ${color}33`,
      borderRadius: 12,
    }}>
      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color, fontFamily: "monospace", letterSpacing: "-0.02em", marginTop: 2 }}>
        {value == null ? "—" : value}
      </div>
    </div>
  );
}

function DetailItem({ label, value, t, mono }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: t.textMuted, fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.5, marginBottom: 3 }}>{label}</div>
      <div style={{ fontSize: 12, color: value ? t.text : t.textMuted, fontFamily: mono ? "monospace" : "inherit", wordBreak: "break-all", lineHeight: 1.45 }}>
        {value || "—"}
      </div>
    </div>
  );
}
